import React, { useMemo, useRef, useContext } from "react";
import { DataTableContext } from "../Tabs/Overview";
import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";
import { ViewMasterCampaignsContext } from "../index";
import LockBudgetConfirmationModal from "../../Modals/LockBudgetConfirmationModal";
import { toggleCampaignBudgetLocked } from "../../../Flux/Actions/MasterCampaignActions";
import { ToastNotification } from "@acadia-ui/toast";
import Tooltip, { TOOLTIP_POSITION } from "@acadia-ui/tooltip";
import { multipleMasterCampaignsSelected } from "./actionValidations";

export default () => {
	// Define a ref that will be used to open and close the modal
	const dpRef = useRef();

	// Import various contexts
	const { checkboxSelections, dataProvider, dataTableApi } = useContext(DataTableContext);
	const { refreshData, enabledPlatformAPIs, platforms, addBanner } = useContext(ViewMasterCampaignsContext);

	const shown = useMemo(() => {
		return (
			enabledPlatformAPIs?.find(({ MasterPlatformID, SyncStatus }) => {
				if (MasterPlatformID == 5) {
					return SyncStatus;
				}
				return false;
			})?.SyncStatus || false
		);
	}, [enabledPlatformAPIs]);

	const disabled = useMemo(() => {
		const sac = checkboxSelections?.selectedActivationCampaigns || [];
		if (shown && sac?.length == 1) {
			const { platform, masterCampaignId, activationCampaignId } = sac?.[0];
			const platformId = platforms?.find(({ PlatformName }) => PlatformName == platform)?.MasterPlatformID;
			const status = dataProvider?.data
				?.find(({ MasterCampaignID }) => MasterCampaignID == masterCampaignId)
				?.ActivationCampaigns?.[platform]?.find(({ CampaignID }) => CampaignID == activationCampaignId)?.ExternalSystemStatusID;

			// Allow prisma platform, status Success
			// See PlatformStatusIcon.jsx for full status map
			if (platformId == 5 && status == 3) {
				return false;
			}
		}
		return true;
	}, [checkboxSelections, shown, enabledPlatformAPIs, dataProvider]);

	const multipleMasterCampaigns = useMemo(() => multipleMasterCampaignsSelected(checkboxSelections), [checkboxSelections]);

	const locked = useMemo(() => {
		const sac = checkboxSelections?.selectedActivationCampaigns || [];
		if (sac?.length == 1) {
			const { platform, masterCampaignId, activationCampaignId } = sac?.[0];

			const campaignData = dataProvider?.data
				?.find(({ MasterCampaignID }) => MasterCampaignID == masterCampaignId)
				?.ActivationCampaigns?.[platform]?.find(({ CampaignID }) => CampaignID == activationCampaignId);

			if (campaignData?.IsPrismaBudgetLocked == "True") {
				return true;
			}
		}
		return false;
	}, [checkboxSelections, dataProvider]);

	const handleSubmit = () => {
		try {
			const lock = !locked;
			const selectedCampaign = checkboxSelections?.selectedActivationCampaigns?.[0];
			toggleCampaignBudgetLocked(selectedCampaign?.activationCampaignId, lock).then(() => {
				refreshData?.({
					onSuccess: () => {
						addBanner(
							<ToastNotification
								type="success"
								title={`${lock ? "L" : "Unl"}ocked Budget`}
								message={`Budget is Successfully ${!lock ? "unlocked" : "Locked"}`}
								timed={5000}
							/>
						);
						dataTableApi?.current?.rowSelectionManager?.tree?.selectNode?.(false);
						setTimeout(() => dataProvider?.update?.(), 1);
					},
					onFailure: () => {
						addBanner(
							<ToastNotification
								type="error"
								title={`Failed to ${!lock && "Un"}lock Budget`}
								message={`Budget was not successfully ${!lock ? "unlocked" : "locked"}`}
							/>
						);
					}
				});
			});
		} catch (e) {
			addBanner(<ToastNotification type="error" title="Failed to lock or unlock Budget" message={`Budget was not successfully locked or unlocked`} />);
		}
	};

	const Trigger = () => {
		if (shown) {
			return (
				<Tooltip title={locked ? "Unlock Budget" : "Lock Budget"} position={TOOLTIP_POSITION.BOTTOM}>
					<Button
						size={BUTTON_SIZE.SMALL}
						display={BUTTON_DISPLAY.ICON_BORDERED}
						icon={locked ? "icon-unlock" : "icon-lock"}
						onClick={(e) => {
							dpRef?.current?.triggerOpenClose?.(e, !locked);
						}}
						disabled={disabled || multipleMasterCampaigns}
						id="toggleBudgetLockButton"
						data-tracking-id-alt={locked ? "unlock-budget-button" : "lock-budget-button"}
					>
						{locked ? "Unlock Budget" : "Lock Budget"}
					</Button>
				</Tooltip>
			);
		}
		return <></>;
	};

	const DisplayedComponent = () => {
		return (
			<LockBudgetConfirmationModal
				lockBudgetModal={(ref) => {
					dpRef.current = ref;
				}}
				action="Cancel"
				onSubmit={handleSubmit}
			/>
		);
	};

	// Return a memoized object containing both of these components
	return useMemo(() => ({ Trigger, DisplayedComponent }), [shown, disabled, checkboxSelections, locked, dataProvider]);
};
